import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { isNil } from 'es-toolkit';

import type { ClientsTableColumn } from '../utils/clients-columns';
import type { ClientsFilters } from './filter-by';
import { clientsColumnsFromSlugs, clientsColumnsToSlugs } from '../utils/clients-columns';

type SortByOrder = 'ASC' | 'DESC';

export type ClientsUrlState = ClientsFilters & {
  householdTagsExcluded?: string[]; // not in ClientsFilters to avoid chip generation
  advisorExcluded?: string[];
  lastContactDaysSinceExcluded?: string[];
  householdTagCategoriesExcluded?: string[];
  primaryAddressStatesExcluded?: string[];
  columns?: ClientsTableColumn[];
  sortBy: string | null;
  sortByOrder: SortByOrder | null;
  after?: string | null;
  before?: string | null;
  pageSize?: number | null;
  currentPage?: number | null;
};

function isValidSortByOrder(value: unknown): value is SortByOrder {
  return value === 'ASC' || value === 'DESC';
}

const VALID_SORT_BY_FIELDS = [
  'name',
  'savvy_aum',
  'advisor_annual_revenue',
  'last_contact_at',
  'service_calendar',
  'next_service_item',
  'updated_at',
  'owner_name',
  'primary_advisor',
];

function isValidSortBy(value: unknown): value is string {
  return typeof value === 'string' && VALID_SORT_BY_FIELDS.includes(value);
}

const VALID_PAGE_SIZES = [5, 10, 25, 50];

function parsePageSize(value: unknown): number | null {
  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    if (Number.isNaN(parsed)) {
      return null;
    }
    return VALID_PAGE_SIZES.includes(parsed) ? parsed : null;
  }
  if (typeof value === 'number') {
    return VALID_PAGE_SIZES.includes(value) ? value : null;
  }
  return null;
}

function parseCurrentPage(value: unknown): number | null {
  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    if (Number.isNaN(parsed) || parsed < 1) {
      return null;
    }
    return parsed;
  }
  if (typeof value === 'number') {
    return value >= 1 ? value : null;
  }
  return null;
}

function parseBooleanFilter(value: unknown): boolean | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return undefined;
}

export function useClientsUrlState(): ClientsUrlState {
  const { query } = useRouter();

  const filters = useMemo(() => {
    const advisor = query['advisor'];
    const search = query['query'];
    const reviewDue = query['reviewDue'];
    const servicesDue = query['servicesDue'];
    const lastContactDaysSince = query['lastContactDaysSince'];
    const householdTags = query['householdTags'];
    const householdTagsExcluded = query['householdTagsExcluded'];
    const householdTagCategories = query['householdTagCategories'];
    const householdTagCategoriesExcluded = query['householdTagCategoriesExcluded'];
    const primaryAddressStates = query['primaryAddressStates'];
    const primaryAddressStatesExcluded = query['primaryAddressStatesExcluded'];
    const advisorExcluded = query['advisorExcluded'];
    const lastContactDaysSinceExcluded = query['lastContactDaysSinceExcluded'];
    const columns = query['columns'];
    const sortBy = query['sortBy'];
    const sortByOrder = query['sortByOrder'];
    const after = query['after'];
    const before = query['before'];
    const pageSize = query['pageSize'];
    const currentPage = query['currentPage'];

    return {
      advisor: typeof advisor === 'string' ? [advisor] : advisor,
      advisorExcluded: typeof advisorExcluded === 'string' ? [advisorExcluded] : advisorExcluded,
      query: typeof search === 'string' ? search : undefined,
      reviewDue: parseBooleanFilter(reviewDue),
      servicesDue: parseBooleanFilter(servicesDue),
      lastContactDaysSince: typeof lastContactDaysSince === 'string' ? [lastContactDaysSince] : lastContactDaysSince,
      lastContactDaysSinceExcluded:
        typeof lastContactDaysSinceExcluded === 'string'
          ? [lastContactDaysSinceExcluded]
          : lastContactDaysSinceExcluded,
      householdTags: typeof householdTags === 'string' ? [householdTags] : householdTags,
      householdTagsExcluded:
        typeof householdTagsExcluded === 'string' ? [householdTagsExcluded] : householdTagsExcluded,
      householdTagCategories:
        typeof householdTagCategories === 'string' ? [householdTagCategories] : householdTagCategories,
      householdTagCategoriesExcluded:
        typeof householdTagCategoriesExcluded === 'string'
          ? [householdTagCategoriesExcluded]
          : householdTagCategoriesExcluded,
      primaryAddressStates: typeof primaryAddressStates === 'string' ? [primaryAddressStates] : primaryAddressStates,
      primaryAddressStatesExcluded:
        typeof primaryAddressStatesExcluded === 'string'
          ? [primaryAddressStatesExcluded]
          : primaryAddressStatesExcluded,
      columns: (() => {
        if (typeof columns === 'string') {
          return clientsColumnsFromSlugs(columns.split(','));
        }

        if (Array.isArray(columns)) {
          return clientsColumnsFromSlugs(columns);
        }

        return undefined;
      })(),
      sortBy: isValidSortBy(sortBy) ? sortBy : null,
      sortByOrder: isValidSortByOrder(sortByOrder) ? sortByOrder : null,
      after: typeof after === 'string' ? after : null,
      before: typeof before === 'string' ? before : null,
      pageSize: parsePageSize(pageSize),
      currentPage: parseCurrentPage(currentPage),
    };
  }, [query]);

  return filters;
}

type ClientsUrlStateAction =
  | { type: 'setAdvisorFilter'; advisor: string[] }
  | { type: 'setQueryFilter'; query: string }
  | { type: 'setReviewDueFilter'; reviewDue: boolean }
  | { type: 'setServicesDueFilter'; servicesDue: boolean }
  | { type: 'setServiceCalendarFilters'; reviewDue: boolean; servicesDue: boolean }
  | { type: 'setLastContactDaysSinceFilter'; lastContactDaysSince: string[] | undefined }
  | { type: 'setHouseholdTagsFilter'; householdTags: string[] }
  | { type: 'setHouseholdTagCategoriesFilter'; householdTagCategories: string[] }
  | { type: 'setPrimaryAddressStates'; primaryAddressStates: string[] | undefined }
  | {
      type: 'setClientFilters';
      reviewDue: boolean | undefined;
      servicesDue: boolean | undefined;
      lastContactDaysSince: string[];
      lastContactDaysSinceExcluded: string[];
      advisor: string[];
      advisorExcluded: string[];
      householdTags: string[];
      householdTagsExcluded: string[];
      householdTagCategories: string[];
      householdTagCategoriesExcluded: string[];
      primaryAddressStates: string[] | undefined;
      primaryAddressStatesExcluded: string[] | undefined;
    }
  | { type: 'setColumns'; columns: ClientsTableColumn[] }
  | { type: 'setSortBy'; sortBy: string }
  | { type: 'clearAllFilters' }
  | {
      type: 'setPagination';
      after: string | null;
      before: string | null;
      pageSize: number | null;
      currentPage: number | null;
    };

export function useClientsUrlStateDispatch() {
  const router = useRouter();
  const query = useClientsUrlState();
  return (action: ClientsUrlStateAction) => {
    const newQuery = clientsUrlStateReducer(query, action);
    const cleanQuery: Record<string, string | string[]> = {};

    if (newQuery.columns && newQuery.columns.length > 0) {
      cleanQuery['columns'] = clientsColumnsToSlugs(newQuery.columns);
    }

    if (newQuery.advisor && newQuery.advisor.length > 0) {
      cleanQuery['advisor'] = newQuery.advisor;
    }
    if (newQuery.advisorExcluded && newQuery.advisorExcluded.length > 0) {
      cleanQuery['advisorExcluded'] = newQuery.advisorExcluded;
    }
    if (newQuery.query) {
      cleanQuery['query'] = newQuery.query;
    }
    if (!isNil(newQuery.reviewDue)) {
      cleanQuery['reviewDue'] = newQuery.reviewDue ? 'true' : 'false';
    }
    if (!isNil(newQuery.servicesDue)) {
      cleanQuery['servicesDue'] = newQuery.servicesDue ? 'true' : 'false';
    }
    if (newQuery.lastContactDaysSince && newQuery.lastContactDaysSince.length > 0) {
      cleanQuery['lastContactDaysSince'] = newQuery.lastContactDaysSince;
    }
    if (newQuery.lastContactDaysSinceExcluded && newQuery.lastContactDaysSinceExcluded.length > 0) {
      cleanQuery['lastContactDaysSinceExcluded'] = newQuery.lastContactDaysSinceExcluded;
    }
    if (newQuery.householdTags && newQuery.householdTags.length > 0) {
      cleanQuery['householdTags'] = newQuery.householdTags;
    }
    if (newQuery.householdTagsExcluded && newQuery.householdTagsExcluded.length > 0) {
      cleanQuery['householdTagsExcluded'] = newQuery.householdTagsExcluded;
    }
    if (newQuery.householdTagCategories && newQuery.householdTagCategories.length > 0) {
      cleanQuery['householdTagCategories'] = newQuery.householdTagCategories;
    }
    if (newQuery.householdTagCategoriesExcluded && newQuery.householdTagCategoriesExcluded.length > 0) {
      cleanQuery['householdTagCategoriesExcluded'] = newQuery.householdTagCategoriesExcluded;
    }
    if (newQuery.primaryAddressStates && newQuery.primaryAddressStates.length > 0) {
      cleanQuery['primaryAddressStates'] = newQuery.primaryAddressStates;
    }
    if (newQuery.primaryAddressStatesExcluded && newQuery.primaryAddressStatesExcluded.length > 0) {
      cleanQuery['primaryAddressStatesExcluded'] = newQuery.primaryAddressStatesExcluded;
    }
    if (newQuery.sortBy) {
      cleanQuery['sortBy'] = newQuery.sortBy;
    }
    if (!isNil(newQuery.sortByOrder)) {
      cleanQuery['sortByOrder'] = newQuery.sortByOrder;
    }
    if (!isNil(newQuery.after)) {
      cleanQuery['after'] = newQuery.after;
    }
    if (!isNil(newQuery.before)) {
      cleanQuery['before'] = newQuery.before;
    }
    if (!isNil(newQuery.pageSize)) {
      cleanQuery['pageSize'] = String(newQuery.pageSize);
    }
    if (!isNil(newQuery.currentPage)) {
      cleanQuery['currentPage'] = String(newQuery.currentPage);
    }

    void router.replace({ query: cleanQuery });
  };
}

function toggleSortByOrder(currentOrder: SortByOrder | null): SortByOrder {
  return currentOrder === 'ASC' ? 'DESC' : 'ASC';
}

function clientsUrlStateReducer(state: ClientsUrlState, action: ClientsUrlStateAction): ClientsUrlState {
  switch (action.type) {
    case 'setAdvisorFilter':
      return {
        ...state,
        advisor: arrFilterValue(action.advisor) ?? undefined,
        after: null,
        before: null,
        currentPage: 1,
      };
    case 'setQueryFilter':
      return {
        ...state,
        query: action.query,
        after: null,
        before: null,
        currentPage: 1,
      };
    case 'setReviewDueFilter':
      return {
        ...state,
        reviewDue: action.reviewDue,
        after: null,
        before: null,
        currentPage: 1,
      };
    case 'setServicesDueFilter':
      return {
        ...state,
        servicesDue: action.servicesDue,
        after: null,
        before: null,
        currentPage: 1,
      };
    case 'setServiceCalendarFilters':
      return {
        ...state,
        reviewDue: action.reviewDue,
        servicesDue: action.servicesDue,
        after: null,
        before: null,
        currentPage: 1,
      };
    case 'setLastContactDaysSinceFilter':
      return {
        ...state,
        lastContactDaysSince: arrFilterValue(action.lastContactDaysSince ?? null) ?? undefined,
        after: null,
        before: null,
        currentPage: 1,
      };
    case 'setHouseholdTagsFilter':
      return {
        ...state,
        householdTags: arrFilterValue(action.householdTags) ?? undefined,
        after: null,
        before: null,
        currentPage: 1,
      };
    case 'setHouseholdTagCategoriesFilter':
      return {
        ...state,
        householdTagCategories: arrFilterValue(action.householdTagCategories) ?? undefined,
        after: null,
        before: null,
        currentPage: 1,
      };
    case 'setPrimaryAddressStates':
      return {
        ...state,
        primaryAddressStates: arrFilterValue(action.primaryAddressStates ?? null) ?? undefined,
        after: null,
        before: null,
        currentPage: 1,
      };
    case 'setClientFilters':
      return {
        ...state,
        reviewDue: action.reviewDue,
        servicesDue: action.servicesDue,
        lastContactDaysSince: arrFilterValue(action.lastContactDaysSince) ?? undefined,
        lastContactDaysSinceExcluded: arrFilterValue(action.lastContactDaysSinceExcluded) ?? undefined,
        advisor: arrFilterValue(action.advisor) ?? undefined,
        advisorExcluded: arrFilterValue(action.advisorExcluded) ?? undefined,
        householdTags: arrFilterValue(action.householdTags) ?? undefined,
        householdTagsExcluded: arrFilterValue(action.householdTagsExcluded) ?? undefined,
        householdTagCategories: arrFilterValue(action.householdTagCategories) ?? undefined,
        householdTagCategoriesExcluded: arrFilterValue(action.householdTagCategoriesExcluded) ?? undefined,
        primaryAddressStates: arrFilterValue(action.primaryAddressStates ?? null) ?? undefined,
        primaryAddressStatesExcluded: arrFilterValue(action.primaryAddressStatesExcluded ?? null) ?? undefined,
        after: null,
        before: null,
        currentPage: 1,
      };
    case 'setColumns':
      return {
        ...state,
        columns: action.columns,
      };
    case 'setSortBy':
      return {
        ...state,
        sortBy: action.sortBy,
        sortByOrder: state.sortBy === action.sortBy ? toggleSortByOrder(state.sortByOrder) : 'DESC',
        after: null,
        before: null,
        currentPage: 1,
      };
    case 'clearAllFilters':
      return {
        advisor: undefined,
        query: undefined,
        reviewDue: undefined,
        servicesDue: undefined,
        lastContactDaysSince: undefined,
        householdTags: undefined,
        householdTagsExcluded: undefined,
        householdTagCategories: undefined,
        householdTagCategoriesExcluded: undefined,
        primaryAddressStates: undefined,
        primaryAddressStatesExcluded: undefined,
        advisorExcluded: undefined,
        lastContactDaysSinceExcluded: undefined,
        columns: undefined,
        sortBy: state.sortBy,
        sortByOrder: state.sortByOrder,
        after: null,
        before: null,
        pageSize: state.pageSize,
        currentPage: 1,
      };
    case 'setPagination':
      return {
        ...state,
        after: action.after,
        before: action.before,
        pageSize: action.pageSize,
        currentPage: action.currentPage,
      };
  }
  return state;
}

function arrFilterValue<T>(arr: T[] | null): T[] | null {
  return arr?.filter((item) => item !== null) ?? null;
}
